/*
*+------------------------------------------------------------------------+
*| Licensed Materials - Property of IBM
*| IBM Cognos Products: BI Dashboard
*| (C) Copyright IBM Corp. 2020, 2021
*|
*| US Government Users Restricted Rights - Use, duplication or disclosure
*| restricted by GSA ADP Schedule Contract with IBM Corp.
*+------------------------------------------------------------------------+
*/

define([
	'underscore',
	'../../../lib/@waca/upgrades/UpgradeBase',
	'./utils/WidgetUpgradeUtils',
	'./utils/VCF_RuleConstants',
	'./utils/VCFUpgradeUtils',
	'dashboard-analytics/dataSources/services/DataSourcesService',
	'../../../lib/@waca/dashboard-common/dist/core/UniqueHashIdBuilder',
	'../../../app/nls/StringResources',
	'../../../app/util/DeepClone'
], (_, UpgradeBase, WidgetUpgradeUtils, VCF_RuleConstants, VCFUpgradeUtils, DataSourcesService, UniqueHashIdBuilder, StringResources) => {

	/**
	* Upgrade Crosstab/Table widgets to the new version based on the Crosstab/Table ConditionalFormatting feature
	**/

	const defaultPaletteLength = 5;
	class CrosstabTable_VCF_Upgrade extends UpgradeBase {

		constructor() {
			super();
			this.VERSION = 1807;
		}

		/**
	* Perform upgrade
	*
	* @param {object} spec - spec to perform upgrade on
	*
	* @return {Promise} Promise to be resolved when upgrade performed
	*/
		up(spec) {
			return Promise.resolve().then(() => {
				if (!WidgetUpgradeUtils.specHasWidgets(spec)) {
					return spec;
				}

				const dataSourcesClass = this._DataSourcesServices || DataSourcesService;
				this.dataSourcesService = new dataSourcesClass({
					features:{
						API: this.data.dashboardApi,
						Logger: this.data.logger
					}
				});
				this.dataSourcesService.initializeSourcesCollection(spec);

				// Upgrade all the Crosstab widgets
				const upgradeVisIds = ['crosstab', 'JQGrid'];
				const upgradeSlotNames = ['values', 'grid_cols'];
				const promises = [];
				const unsupportedCFs = [];
				_.each(spec.widgets, widget => {
					if (widget && widget.visId && upgradeVisIds.includes(widget.visId)) {
						const dataItems = widget.data && widget.data.dataViews && widget.data.dataViews.length ? widget.data.dataViews[0].dataItems : [];
						// Find color heat dataItem
						const heatSlot = this._getSlot(widget.slotmapping.slots, ['heat']);
						let heatId = heatSlot && heatSlot.dataItems[0];
						let heatDataItem = dataItems.find(dataItem => {
							return (dataItem.id === heatId);
						});
						if (heatDataItem) {
							const widgetUISpecs = this._getObjects(spec.layout, 'id', widget.id);
							const widgetUISpec = widgetUISpecs.length > 0 ? widgetUISpecs[0] : undefined;
							const conditionalFormats = [];
							if (widgetUISpec) {
								widgetUISpec.content = widgetUISpec.content || {};
								widgetUISpec.content.conditionalFormatting = {
									conditionalFormats: conditionalFormats
								};
							}

							let useNewHeatId = false;
							let getDataItemTypes;
							let upgradeSlotContainsHeat = false;
							const upgradeSlotDataItems = [];
							if (widget.visId === 'JQGrid') {
								getDataItemTypes = this._getDataItemTypes(widget);
							} else {
								getDataItemTypes = Promise.resolve();
							}
							promises.push(
								getDataItemTypes.then(dataItemTypes => {
									const upgradeSlot = this._getSlot(widget.slotmapping.slots, upgradeSlotNames);
									if (upgradeSlot) {
										upgradeSlot.dataItems.forEach(id => {
											dataItems.forEach(dataItem => {
												if (dataItem.id == id) {
													upgradeSlotDataItems.push(dataItem);
													if (dataItem.itemId === heatDataItem.itemId) {
														upgradeSlotContainsHeat = true;
													}
												}
											});
										});
									}
									const firstFactInUpgradeSlotIdx = dataItemTypes && upgradeSlotDataItems.findIndex((dataItem) => dataItemTypes[dataItem.itemId] === 'fact');
									const upgradeCFPromises = [];
									upgradeSlotDataItems.forEach((dataItem, idx) => {
										if (useNewHeatId) {
										//We need a 1-1 relationship
											heatDataItem = this._cloneDataItemWithNewId(heatDataItem);
											heatSlot.dataItems.push(heatDataItem.id);
											dataItems.push(heatDataItem);
										}
										useNewHeatId = true;
										const currentType = dataItemTypes && dataItemTypes[dataItem.itemId];
										let upgradeCFPromise = Promise.resolve();
										if (widget.visId === 'crosstab' || currentType === 'fact') {
											upgradeCFPromise = this._createConditionFormat(widget, conditionalFormats, dataItem, heatDataItem, upgradeSlotContainsHeat);
										} else if (upgradeSlotContainsHeat) {
											if (dataItem.itemId === heatDataItem.itemId) {
												unsupportedCFs.push({
													widget: widget.visId,
													dataItem: dataItem
												});
											}
										} else {
											const nextIdx = idx+1;
											if (firstFactInUpgradeSlotIdx === -1 || firstFactInUpgradeSlotIdx === nextIdx) {
											//Either there are no fact columns or It's a non grouped attribute. (Groups aren't colored)
												unsupportedCFs.push({
													widget: widget.id,
													dataItem: dataItem
												});
											}
										}
										upgradeCFPromises.push(upgradeCFPromise);
									});
									return Promise.all(upgradeCFPromises);
								})
							);
						}
					}
				});
				return Promise.all(promises).then(()=> {
					if (unsupportedCFs.length) {
						const msg =  StringResources.get('unsupportedConditionalFormattingEncountered');
						var logger = this.data.logger;
						if (logger) {
							logger.warn(msg, unsupportedCFs);
						}
						this._showUnsupportedCFMessage(msg);
					}
					return spec;
				});
			});
		}

		_showUnsupportedCFMessage(msg) {
			this.data.dashboardApi.showToast(msg, {
				type: 'warning',
				preventDuplicates: true
			});
		}

		_createConditionFormat(widget, conditionalFormats, dataItem, heatDataItem, upgradeSlotContainsHeat) {
			const conditionalFormat = {
				'dataItemId': dataItem.itemId,
				'paletteId': 'condColorPalette0',
				'heatId': heatDataItem.id,
				'scale': VCF_RuleConstants.RULE_SCALES.NUMERIC,
				'rules': [],
				'backgroundOpacity': 100
			};
			const colors = widget.conditions && widget.conditions.palette ? widget.conditions.palette.colors : [];
			let promise;
			if (!upgradeSlotContainsHeat || (heatDataItem.itemId === dataItem.itemId)) {
				promise = this._updateTargetConditionalFormat(widget, colors, conditionalFormat);
			}else{
				promise = Promise.resolve();
			}
			return promise.then(() => {
				conditionalFormats.push(conditionalFormat);
				// delete this from dashboard spec
				delete widget.conditions;
			});
		}

		_cloneDataItemWithNewId(dataItem) {
			const clonedItem = JSON.parse(JSON.stringify(dataItem));
			clonedItem.id = UniqueHashIdBuilder.createUniqueHashId(clonedItem.id + _.uniqueId(), []);
			return clonedItem;
		}

		// Downgrade is not available in CA
		down(spec) {
			return Promise.resolve(spec);
		}

		_getObjects(obj, key, val) {
			let objects = [];
			for (const i in obj) {
				if (!obj.hasOwnProperty(i)) continue;
				if (typeof obj[i] == 'object') {
					objects = objects.concat(this._getObjects(obj[i], key, val));
				} else
				if (i == key && obj[i] == val) {
					objects.push(obj);
				}
			}
			return objects;
		}

		_getSlot(slots, slotNamesFilter) {
			return _.find(slots, (slot) => {
				return slotNamesFilter.includes(slot.name);
			});
		}

		_updateTargetConditionalFormat(widget, colors, conditionalFormat) {
			if (_.isEmpty(colors)) {
				return Promise.resolve();
			}

			const condColorPaletteProperty = this._getObjects(widget.properties, 'id', 'condColorPalette');
			if (condColorPaletteProperty && condColorPaletteProperty.length) {
				conditionalFormat.paletteId = condColorPaletteProperty[0].value;
			}

			const condColorOrderProperty = this._getObjects(widget.properties, 'id', 'condColorOrder');
			const isPaletteReversed = condColorOrderProperty && condColorOrderProperty.length ? condColorOrderProperty[0].value === 'DarkerForLowerValue' : false;
			let rule = null;

			const rangeRuleTemplate = {
				'ruleType': VCF_RuleConstants.RULE_TYPES.RANGE,
				'startCondition': {
					'type': VCF_RuleConstants.RULE_OPERATORS.greaterThanOrEqualTo,
					'value': undefined
				},
				'ruleStyle': {
					'fillColor': '',
					'textColor': '',
					'shape': ''
				},
				'operator': VCF_RuleConstants.RangeOperators.and,
				'endCondition': {
					'type': VCF_RuleConstants.RULE_OPERATORS.lessThan,
					'value': undefined
				}
			};

			const paletteLengthPromise = conditionalFormat.paletteId.startsWith('condColorPalette') ? Promise.resolve(defaultPaletteLength) : this.data.dashboardApi.getFeature('Palette').getPalette(conditionalFormat.paletteId).then((condColorPalette) => {
				const paletteColors = condColorPalette.content.fills;
				return paletteColors.length;
			}, () => {
				return defaultPaletteLength;
			});
			return paletteLengthPromise.then((paletteLength) => {
				const rangesLength = colors.length - 1;
				const colorIndexMap = VCFUpgradeUtils.buildIndexMap(paletteLength, rangesLength);
				if(isPaletteReversed){
					colorIndexMap.reverse();
				}
				const defaultFillColors = _.range(paletteLength).map((index) => {
					return 'color' + index;
				});
				for(let i = 0; i < rangesLength; i++) {
					rule = _.deepClone(rangeRuleTemplate);
					rule.startCondition.value = colors[i].value;
					rule.endCondition.value = colors[i + 1].value;
					if (i < defaultFillColors.length) {
						rule.ruleStyle.fillColor = defaultFillColors[colorIndexMap[i]];
					} else {
						rule.ruleStyle.fillColor = defaultFillColors[colorIndexMap[i % defaultFillColors.length]];
					}
					conditionalFormat.rules.push(rule);
				}
			});
		}

		_getDataItemTypes(widget) {
			const dataTypes = {};
			let dataViews = widget.data && widget.data.dataViews;
			let modelRef = dataViews && dataViews[0] ? dataViews[0].modelRef : null;
			const promise = (!modelRef || !dataViews) ? Promise.resolve(null) : this._getModule(modelRef);
			return promise.then(module => {
				if (module) {
					_.each(dataViews, dataView => {
						let dataItems = dataView.dataItems;
						if (dataItems){
							_.each(dataItems, dataItem =>{
								// get metadata with column id
								const mdc = module.getMetadataColumn(dataItem.itemId);
								dataTypes[dataItem.itemId] = mdc.getType();
							});
						}
					});
				}
				return dataTypes;
			});
		}

		_getModule(modelRef) {
			return this.dataSourcesService.getModule(modelRef);
		}

	}

	return new CrosstabTable_VCF_Upgrade();
});
